import {
  CatalogEventRecord,
  CatalogEventType,
  CatalogEventTypeLabels,
  CatalogObservationSource,
  CatalogObservationSourceLabels
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiAsyncError } from 'noddi-types';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NavioListCard, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { getLabel, getLabelMapFromEntries, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useState } from 'react';

const PAGE_SIZE = 25;

const eventTypeLabels = getLabelMapFromEntries(CatalogEventType, CatalogEventTypeLabels);
const sourceLabels = getLabelMapFromEntries(CatalogObservationSource, CatalogObservationSourceLabels);

const EVENT_ICON: Record<CatalogEventType, NoddiIconName> = {
  [CatalogEventType.NODE_CREATED]: 'Plus',
  [CatalogEventType.NODE_CHANGED]: 'Wrench',
  [CatalogEventType.NODE_RETIRED]: 'Archive',
  [CatalogEventType.OBSERVATION_RECORDED]: 'Eye',
  [CatalogEventType.OBSERVATION_SUPERSEDED]: 'RefreshCcw',
  [CatalogEventType.CONFLICT_DETECTED]: 'TriangleAlert',
  [CatalogEventType.CAR_ASSIGNED]: 'Car',
  [CatalogEventType.CAR_REASSIGNED]: 'ArrowLeftRight',
  [CatalogEventType.CAR_UNASSIGNED]: 'X',
  [CatalogEventType.MODIFICATION_ASSIGNED]: 'Layers',
  [CatalogEventType.MODIFICATION_REMOVED]: 'Minus',
  [CatalogEventType.OVERRIDE_ADDED]: 'ClipboardList',
  [CatalogEventType.OVERRIDE_REMOVED]: 'Undo2'
};

const FIELD_LABEL: Record<string, string> = {
  attribute: 'Attribute',
  note: 'Note',
  override_id: 'Override',
  unit: 'Unit',
  value: 'Value',
  wheel_option_axle_id: 'Axle'
};

type FieldChange = {
  field: string;
  before: string | null;
  after: string | null;
  hasBefore: boolean;
  hasAfter: boolean;
};

type Props = { carId: number } | { nodeId: number };

type EventListProps = {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  events: CatalogEventRecord[];
  totalCount: number | undefined;
  isPending: boolean;
  error: NoddiAsyncError | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onLoadMore: () => void;
  showPlate: boolean;
};

export const CatalogEventTimeline = (props: Props) => {
  if ('carId' in props) {
    return <CarEvents carId={props.carId} />;
  }
  return <NodeEvents nodeId={props.nodeId} />;
};

const CarEvents = ({ carId }: { carId: number }) => {
  const [open, setOpen] = useState(false);
  const { data, isPending, error, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
    noddiAsync.useInfiniteGet({
      type: URLKeys.getPaginatedCarCatalogEvents,
      input: { carId, pageSize: PAGE_SIZE },
      queryConfig: { enabled: open }
    });

  return (
    <EventList
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      events={data?.pages.flatMap((page) => page.results) ?? []}
      totalCount={data?.pages[0]?.count}
      isPending={isPending}
      error={error}
      hasNextPage={Boolean(hasNextPage)}
      isFetchingNextPage={isFetchingNextPage}
      isFetchNextPageError={isFetchNextPageError}
      onLoadMore={() => void fetchNextPage()}
      showPlate={false}
    />
  );
};

const NodeEvents = ({ nodeId }: { nodeId: number }) => {
  const [open, setOpen] = useState(false);
  const { data, isPending, error, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
    noddiAsync.useInfiniteGet({
      type: URLKeys.getPaginatedCatalogNodeEvents,
      input: { nodeId, pageSize: PAGE_SIZE },
      queryConfig: { enabled: open }
    });

  return (
    <EventList
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      events={data?.pages.flatMap((page) => page.results) ?? []}
      totalCount={data?.pages[0]?.count}
      isPending={isPending}
      error={error}
      hasNextPage={Boolean(hasNextPage)}
      isFetchingNextPage={isFetchingNextPage}
      isFetchNextPageError={isFetchNextPageError}
      onLoadMore={() => void fetchNextPage()}
      showPlate
    />
  );
};

const EventList = ({
  open,
  onOpen,
  onClose,
  events,
  totalCount,
  isPending,
  error,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onLoadMore,
  showPlate
}: EventListProps) => {
  const title = totalCount == null ? 'Catalog history' : `Catalog history (${totalCount})`;
  const showLoadMore = hasNextPage || isFetchNextPageError;

  return (
    <>
      <NoddiButton variant='outline' size='small' startIcon='Activity' onClick={onOpen}>
        Catalog history
      </NoddiButton>
      <NoddiDialog
        open={open}
        onClose={onClose}
        title={title}
        maxWidth='md'
        dialogHeaderClassName='sr-only'
        // md: repeats the cap so it wins over the dialog's own md:max-h-[85vh].
        className='max-h-110 overflow-hidden md:max-h-110'
        contentClassName='flex min-h-0 flex-1 flex-col overflow-hidden'
        footer={
          showLoadMore && (
            <div className='flex flex-col items-start gap-4'>
              {isFetchNextPageError && error && (
                <p className='text-sm text-destructive'>Could not load older events.</p>
              )}
              {hasNextPage && (
                <NoddiButton variant='outline' size='small' loading={isFetchingNextPage} onClick={onLoadMore}>
                  Load more
                </NoddiButton>
              )}
            </div>
          )
        }
      >
        <NavioListCard
          title={title}
          icon='Activity'
          size='small'
          hideCount
          fillHeight
          className='min-h-0 flex-1'
          items={events}
          getItemKey={(event) => event.id}
          isPending={open && isPending}
          error={open && events.length === 0 ? error : null}
          translations={{ noItems: 'No catalog history yet.' }}
          renderItem={(event) => <EventRow event={event} showPlate={showPlate} />}
        />
      </NoddiDialog>
    </>
  );
};

const EventRow = ({ event, showPlate }: { event: CatalogEventRecord; showPlate: boolean }) => {
  const trimmedActor = event.actorName?.trim();
  const actorName = trimmedActor ? trimmedActor : 'System';
  const timestamp = noddiFormatDate(event.createdAt, NoddiDateFormats.DATE_TIME);
  const context = contextParts(event, showPlate);
  const changes = fieldChanges(event);

  const changeLine = changes.map(changeText).join(' · ');

  return (
    <div className='flex min-w-0 flex-1 flex-wrap items-center gap-4 text-sm'>
      <NoddiIcon name={EVENT_ICON[event.eventType]} size='small' className='shrink-0 text-muted-foreground' />
      <span className='font-medium'>{actorName}</span>
      <span>{getLabel(event.eventType, eventTypeLabels)}</span>
      <span className='text-xs text-muted-foreground'>{timestamp}</span>
      <span className='text-xs text-muted-foreground'>{getLabel(event.source, sourceLabels)}</span>
      {context.length > 0 && <span className='text-xs text-muted-foreground'>{context.join(' · ')}</span>}
      {changeLine.length > 0 && <span className='min-w-0 text-xs break-words text-muted-foreground'>{changeLine}</span>}
    </div>
  );
};

const contextParts = (event: CatalogEventRecord, showPlate: boolean): string[] => {
  const parts: string[] = [];
  if (showPlate && event.licensePlateNumber) {
    parts.push(event.licensePlateNumber);
  }
  if (event.modification?.name) {
    parts.push(event.modification.name);
  }
  if (event.nodeFrom) {
    parts.push(`${event.nodeFrom.name} to ${event.node.name}`);
  } else if (!showPlate && !event.modification) {
    parts.push(event.node.name);
  }
  return parts;
};

const fieldChanges = (event: CatalogEventRecord): FieldChange[] => {
  const byField = new Map<string, FieldChange>();
  for (const item of event.beforeValues) {
    byField.set(item.field, {
      field: item.field,
      before: item.value,
      after: null,
      hasBefore: true,
      hasAfter: false
    });
  }
  for (const item of event.afterValues) {
    const existing = byField.get(item.field);
    byField.set(item.field, {
      field: item.field,
      before: existing?.before ?? null,
      after: item.value,
      hasBefore: existing?.hasBefore ?? false,
      hasAfter: true
    });
  }
  return [...byField.values()].filter(
    (change) => !change.hasBefore || !change.hasAfter || change.before !== change.after
  );
};

const changeText = (change: FieldChange) => {
  const label = fieldLabel(change.field);
  if (change.hasBefore && change.hasAfter) {
    return `${label} ${displayValue(change.before)} → ${displayValue(change.after)}`;
  }
  return `${label} ${displayValue(change.hasAfter ? change.after : change.before)}`;
};

const fieldLabel = (field: string) =>
  FIELD_LABEL[field] ??
  field
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const displayValue = (value: string | null) => value ?? 'Cleared';
